import * as React from 'react'
import { getConfiguration } from '../utils';

interface PreviewComponentProps {
  configurations: any;
  data: any;
  select?: any;
}

const mockSurveyData = {
  survey: {
    id: '1',
    question: 'What type of content would you like to see more of in our newsletter?',
    is_multi_select: false,
    total_votes: 245,
    created_at: '2024-01-15T10:30:00Z'
  },
  options: [
    {
      id: '1',
      option_text: 'Technology and Innovation Updates',
      vote_count: 98,
      percentage: 40.0,
      is_correct: false
    },
    {
      id: '2',
      option_text: 'Industry News and Analysis',
      vote_count: 73,
      percentage: 29.8,
      is_correct: false
    },
    {
      id: '3',
      option_text: 'Expert Interviews and Insights',
      vote_count: 49,
      percentage: 20.0,
      is_correct: false
    },
    {
      id: '4',
      option_text: 'Product Reviews and Recommendations',
      vote_count: 25,
      percentage: 10.2,
      is_correct: false
    }
  ]
};

const getResultCardStyle = (configurations: any, percentage: number) => {
  const cardSize = getConfiguration(configurations, 'survey_results_card_size', 'large');
  const borderColor = getConfiguration(configurations, 'survey_results_border_color', '#dee2e6');
  const highlightColor = getConfiguration(configurations, 'survey_results_highlight_color', '#7FAE1B');
  
  const isHighPercentage = percentage > 30;
  
  return {
    transition: 'all 0.3s ease',
    borderRadius: '8px',
    border: `1px solid ${isHighPercentage ? highlightColor : borderColor}`,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
    height: cardSize === 'small' ? 'auto' : '100%',
    minHeight: cardSize === 'small' ? '80px' : '120px',
    backgroundColor: isHighPercentage ? '#f8f9fa' : '#ffffff',
    marginBottom: '1rem',
    padding: cardSize === 'small' ? '0.75rem' : '1rem'
  };
};

const ResultCard = ({ option, configurations }: { option: any; configurations: any }) => {
  const cardStyle = getResultCardStyle(configurations, option.percentage);
  const highlightColor = getConfiguration(configurations, 'survey_results_highlight_color', '#7FAE1B');
  const showPercentage = getConfiguration(configurations, 'survey_results_show_percentage', 'true') === 'true';
  const showVoteCount = getConfiguration(configurations, 'survey_results_show_vote_count', 'true') === 'true';
  const showProgressBar = getConfiguration(configurations, 'survey_results_show_progress_bar', 'true') === 'true';
  const decimalPlaces = parseInt(getConfiguration(configurations, 'survey_results_decimal_places', '1'), 10);

  return (
    <div style={cardStyle}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        <h5 style={{
          margin: 0,
          marginBottom: '0.5rem',
          fontWeight: 500,
          color: '#343a40',
          fontSize: '1rem'
        }}>
          {option.option_text}
        </h5>
        
        <div style={{ marginTop: 'auto' }}>
          {showProgressBar && (
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              overflow: 'hidden',
              marginBottom: '0.5rem'
            }}>
              <div style={{
                width: `${option.percentage}%`,
                height: '100%',
                backgroundColor: highlightColor,
                borderRadius: '4px',
                transition: 'width 0.5s ease'
              }}></div>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.9rem'
          }}>
            {showPercentage && (
              <span style={{
                fontWeight: 600,
                color: highlightColor,
                fontSize: '1.4rem'
              }}>
                {option.percentage.toFixed(decimalPlaces)}%
              </span>
            )}
            {showVoteCount && (
              <span style={{ color: '#6c757d' }}>
                {option.vote_count} votes
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PreviewComponent: React.FC<PreviewComponentProps> = ({ configurations, data, select }) => {
  const configData = configurations || data || {};
  const headerShow = getConfiguration(configData, 'survey_results_header_show', 'true') === 'true';
  const pageTitle = getConfiguration(configData, 'survey_results_page_title', 'Survey Results');
  const headerDescription = getConfiguration(configData, 'survey_results_header_description', 
    'See how others responded to this survey. Results are updated in real-time.');
  const headerImageUrl = getConfiguration(configData, 'survey_results_header_image_url', '') ||
                        getConfiguration(configData, 'brand_header_image_url', '');
  const showStats = getConfiguration(configData, 'survey_results_show_stats', 'true') === 'true';
  const showSurveyInfo = getConfiguration(configData, 'survey_results_show_survey_info', 'true') === 'true';
  const highlightColor = getConfiguration(configData, 'survey_results_highlight_color', '#7FAE1B');
  
  // Message preview settings
  const messagePreview = getConfiguration(configData, 'survey_results_show_message_preview', 'none');
  const successBgColor = getConfiguration(configData, 'survey_results_success_bg_color', '#d4edda');
  const successTextColor = getConfiguration(configData, 'survey_results_success_text_color', '#155724');
  const failBgColor = getConfiguration(configData, 'survey_results_fail_bg_color', '#f8d7da');
  const failTextColor = getConfiguration(configData, 'survey_results_fail_text_color', '#721c24');

  const sectionStyle = {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
    border: '1px solid #f0f0f0',
    borderLeft: `4px solid ${highlightColor}`,
  };

  return (
    <div style={{ padding: '2rem', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {headerShow && (
        <>
          {headerImageUrl && (
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <img 
                src={headerImageUrl} 
                alt="Header" 
                style={{ maxHeight: '80px' }} 
              />
            </div>
          )}
          <header style={{
            textAlign: 'center',
            marginBottom: '1rem',
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#2c3e50'
          }}>
            {pageTitle}
          </header>
          <div style={{
            textAlign: 'center',
            color: '#6c757d',
            marginBottom: '2rem',
            lineHeight: 1.6,
            fontSize: '1.1rem',
            maxWidth: '600px',
            margin: '0 auto 2rem'
          }}
          dangerouslySetInnerHTML={{ __html: headerDescription }}>
          </div>
        </>
      )}

      {/* Success/Fail Message Preview */}
      {messagePreview !== 'none' && (
        <div style={{
          ...sectionStyle,
          backgroundColor: messagePreview === 'success' ? successBgColor : failBgColor,
          color: messagePreview === 'success' ? successTextColor : failTextColor,
          borderLeft: `4px solid ${messagePreview === 'success' ? successTextColor : failTextColor}`,
          textAlign: 'center',
          fontSize: '1.1rem',
          fontWeight: 500
        }}>
          {messagePreview === 'success' ? (
            <>
              <h4 style={{ color: successTextColor, marginBottom: '0.5rem' }}>🎉 Correct!</h4>
              <p style={{ margin: 0, color: successTextColor }}>
                Great job! You selected the right answer. This survey will help us improve our content.
              </p>
            </>
          ) : (
            <>
              <h4 style={{ color: failTextColor, marginBottom: '0.5rem' }}>❌ Not quite right</h4>
              <p style={{ margin: 0, color: failTextColor }}>
                That wasn't the correct answer, but thanks for participating! Check out the results below.
              </p>
            </>
          )}
        </div>
      )}

      {/* Survey Question Section */}
      <div style={sectionStyle}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#495057' }}>Survey Question</h3>
          <h4 style={{
            color: '#495057',
            marginBottom: '1rem',
            fontSize: '1.2rem',
            fontWeight: 400
          }}>
            {mockSurveyData.survey.question}
          </h4>
          {showSurveyInfo && (
            <div style={{
              display: 'flex',
              gap: '2rem',
              alignItems: 'center',
              fontSize: '0.9rem',
              color: '#6c757d',
              flexWrap: 'wrap'
            }}>
              <div>
                <strong>Total Responses:</strong> {mockSurveyData.survey.total_votes}
              </div>
              <div>
                <strong>Survey Type:</strong> {mockSurveyData.survey.is_multi_select ? 'Multiple Choice' : 'Single Choice'}
              </div>
              <div>
                <strong>Created:</strong> {new Date(mockSurveyData.survey.created_at).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div style={sectionStyle}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#495057' }}>Response Breakdown</h3>
          <div style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
            Here's how people responded to this survey:
          </div>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1rem'
        }}>
          {mockSurveyData.options.map((option) => (
            <ResultCard key={option.id} option={option} configurations={configData} />
          ))}
        </div>
      </div>

      {/* Stats Section */}
      {showStats && (
        <div style={sectionStyle}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#495057' }}>Quick Stats</h3>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ color: highlightColor, marginBottom: '0.5rem' }}>
                40.0%
              </h4>
              <small style={{ color: '#6c757d' }}>Top Choice</small>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ color: '#17a2b8', marginBottom: '0.5rem' }}>
                4
              </h4>
              <small style={{ color: '#6c757d' }}>Total Options</small>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ color: '#ffc107', marginBottom: '0.5rem' }}>
                25.0%
              </h4>
              <small style={{ color: '#6c757d' }}>Average</small>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ color: '#dc3545', marginBottom: '0.5rem' }}>
                4
              </h4>
              <small style={{ color: '#6c757d' }}>Options with Votes</small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};